import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Baby, ChevronRight, ChevronLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    parent_name: '', email: '', phone: '', address: '', city: '', state: '', zip: '',
    preferred_contact: 'phone',
    scheduled_tour: 'no', tour_preferred_date: '', tour_preferred_time: '',
    emergency_contacts: [{ name: '', phone: '', relationship: '' }, { name: '', phone: '', relationship: '' }],
    child_name: '', child_age: '', nickname: '', favorite_food: '', favorite_toy: '', favorite_toy_name: '',
    has_allergies: 'no', allergies: '',
    message: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setEmergency = (idx, key, val) => {
    const contacts = [...form.emergency_contacts];
    contacts[idx] = { ...contacts[idx], [key]: val };
    set('emergency_contacts', contacts);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        parent_name: form.parent_name, email: form.email, phone: form.phone,
        address: form.address, city: form.city, state: form.state, zip: form.zip,
        preferred_contact: form.preferred_contact,
        scheduled_tour: form.scheduled_tour, tour_preferred_date: form.tour_preferred_date, tour_preferred_time: form.tour_preferred_time,
        emergency_name: form.emergency_contacts[0]?.name || '',
        emergency_phone: form.emergency_contacts[0]?.phone || '',
        emergency_relation: form.emergency_contacts[0]?.relationship || '',
        emergency2_name: form.emergency_contacts[1]?.name || '',
        emergency2_phone: form.emergency_contacts[1]?.phone || '',
        emergency2_relation: form.emergency_contacts[1]?.relationship || '',
        child_name: form.child_name, child_age: parseInt(form.child_age) || 0,
        age_group: parseInt(form.child_age) <= 2 ? 'infant' : parseInt(form.child_age) <= 4 ? 'toddler' : 'preschool',
        start_date: new Date().toISOString().split('T')[0],
        nickname: form.nickname, favorite_food: form.favorite_food,
        favorite_toy: form.favorite_toy, toy_name: form.favorite_toy_name,
        has_allergies: form.has_allergies, allergies: form.has_allergies === 'yes' ? form.allergies : '',
        message: form.message,
      };
      await axios.post(`${API}/enrollments`, payload);
      setSubmitted(true);
      toast.success('Enrollment submitted!');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" data-testid="mobile-register-success">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 15 }}>
          <div className="w-20 h-20 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
        </motion.div>
        <h2 className="font-fredoka font-bold text-xl text-foreground mb-2">Enrollment Submitted!</h2>
        <p className="text-sm text-muted-foreground mb-6">We'll review your application and be in touch soon.</p>
        <Button onClick={() => navigate('/mobile')} className="bg-secondary hover:bg-secondary/90 text-white rounded-xl" data-testid="mobile-register-home">
          Back to Home
        </Button>
      </div>
    );
  }

  const timeSlots = [
    { value: 'morning-9', label: '9:00 AM' }, { value: 'morning-10', label: '10:00 AM' },
    { value: 'morning-11', label: '11:00 AM' }, { value: 'afternoon-1', label: '1:00 PM' },
    { value: 'afternoon-2', label: '2:00 PM' }, { value: 'afternoon-3', label: '3:00 PM' },
  ];

  return (
    <div className="pb-6" data-testid="mobile-register-page">
      {/* Progress */}
      <div className="px-4 pt-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-secondary' : 'bg-border/40'} transition-colors`} />
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-secondary' : 'bg-border/40'} transition-colors`} />
        </div>
        <p className="text-xs text-muted-foreground text-center">Step {step} of 2</p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: step === 1 ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: step === 1 ? 20 : -20 }} transition={{ duration: 0.2 }}>

          {/* Step 1: Parent Info */}
          {step === 1 && (
            <div className="px-4 space-y-4">
              <div className="text-center mb-2">
                <h2 className="font-fredoka font-bold text-xl text-foreground" data-testid="mobile-register-heading">Parent Information</h2>
                <p className="text-xs text-muted-foreground">Tell us about yourself</p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm flex items-center gap-2"><UserPlus className="w-4 h-4 text-secondary" /> Your Details</h3>
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Name *</Label>
                  <Input value={form.parent_name} onChange={(e) => set('parent_name', e.target.value)} placeholder="Jane Doe" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-parent-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Phone *</Label>
                    <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(503) 555-1234" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-phone" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Address</Label>
                  <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="123 Main St" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-address" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">City</Label>
                    <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Portland" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-city" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">State</Label>
                    <Input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="OR" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-state" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">ZIP</Label>
                    <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} placeholder="97201" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-zip" />
                  </div>
                </div>
              </div>

              {/* Tour Scheduling */}
              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm">Did you schedule a tour?</h3>
                <div className="flex gap-2">
                  {['yes', 'no'].map(v => (
                    <button key={v} type="button" onClick={() => set('scheduled_tour', v)}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${form.scheduled_tour === v ? 'bg-secondary text-white' : 'bg-muted/50 text-muted-foreground border border-border/40'}`}
                      data-testid={`mobile-reg-tour-${v}`}
                    >{v === 'yes' ? 'Yes' : 'No'}</button>
                  ))}
                </div>
                {form.scheduled_tour === 'no' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2 pt-2">
                    <p className="text-xs text-muted-foreground">Would you like to schedule one?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="date" value={form.tour_preferred_date} onChange={(e) => set('tour_preferred_date', e.target.value)} className="rounded-xl h-10 text-sm" data-testid="mobile-reg-tour-date" />
                      <Select value={form.tour_preferred_time} onValueChange={(v) => set('tour_preferred_time', v)}>
                        <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-reg-tour-time"><SelectValue placeholder="Time" /></SelectTrigger>
                        <SelectContent>{timeSlots.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Emergency Contacts */}
              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 text-primary" /> Emergency Contacts</h3>
                {form.emergency_contacts.map((c, i) => (
                  <div key={i} className="space-y-2 pb-2 border-b border-border/20 last:border-0 last:pb-0">
                    <p className="text-xs text-muted-foreground font-semibold">Contact {i + 1}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Name" value={c.name} onChange={(e) => setEmergency(i, 'name', e.target.value)} className="rounded-xl h-10 text-sm" data-testid={`mobile-reg-emerg-name-${i}`} />
                      <Input placeholder="Phone" value={c.phone} onChange={(e) => setEmergency(i, 'phone', e.target.value)} className="rounded-xl h-10 text-sm" data-testid={`mobile-reg-emerg-phone-${i}`} />
                    </div>
                    <Input placeholder="Relationship" value={c.relationship} onChange={(e) => setEmergency(i, 'relationship', e.target.value)} className="rounded-xl h-10 text-sm" data-testid={`mobile-reg-emerg-rel-${i}`} />
                  </div>
                ))}
              </div>

              <Button
                onClick={() => { if (!form.parent_name || !form.email || !form.phone) { toast.error('Please fill in required fields'); return; } setStep(2); }}
                className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-xl py-5 font-bold"
                data-testid="mobile-reg-next"
              >
                Next: Child Info <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: Child Info */}
          {step === 2 && (
            <div className="px-4 space-y-4">
              <div className="text-center mb-2">
                <h2 className="font-fredoka font-bold text-xl text-foreground">Child Information</h2>
                <p className="text-xs text-muted-foreground">Tell us about your little one</p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm flex items-center gap-2"><Baby className="w-4 h-4 text-accent-foreground" /> Basic Info</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Child's Name *</Label>
                    <Input value={form.child_name} onChange={(e) => set('child_name', e.target.value)} placeholder="First name" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-child-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Age *</Label>
                    <Input value={form.child_age} onChange={(e) => set('child_age', e.target.value)} placeholder="e.g. 3" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-child-age" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nickname</Label>
                  <Input value={form.nickname} onChange={(e) => set('nickname', e.target.value)} placeholder="What do they go by?" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-nickname" />
                </div>
              </div>

              {/* Fun Facts */}
              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm">
                  {form.child_name ? `Tell us more about ${form.child_name}` : 'Fun Facts'}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Favorite Food</Label>
                    <Input value={form.favorite_food} onChange={(e) => set('favorite_food', e.target.value)} placeholder="e.g. Mac & cheese" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-food" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Favorite Toy</Label>
                    <Input value={form.favorite_toy} onChange={(e) => set('favorite_toy', e.target.value)} placeholder="e.g. Teddy bear" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-toy" />
                  </div>
                </div>
                {form.favorite_toy && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Toy's Name</Label>
                    <Input value={form.favorite_toy_name} onChange={(e) => set('favorite_toy_name', e.target.value)} placeholder="Does it have a name?" className="rounded-xl h-10 text-sm" data-testid="mobile-reg-toy-name" />
                  </div>
                )}
              </div>

              {/* Allergies */}
              <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                <h3 className="font-fredoka font-semibold text-sm">Allergies?</h3>
                <div className="flex gap-2">
                  {['no', 'yes'].map(v => (
                    <button key={v} type="button" onClick={() => set('has_allergies', v)}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${form.has_allergies === v ? (v === 'yes' ? 'bg-destructive text-white' : 'bg-success text-white') : 'bg-muted/50 text-muted-foreground border border-border/40'}`}
                      data-testid={`mobile-reg-allergy-${v}`}
                    >{v === 'yes' ? 'Yes' : 'No'}</button>
                  ))}
                </div>
                {form.has_allergies === 'yes' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                    <Textarea value={form.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="Please describe allergies..." rows={2} className="rounded-xl text-sm resize-none" data-testid="mobile-reg-allergy-detail" />
                  </motion.div>
                )}
              </div>

              {/* Message */}
              <div className="space-y-1.5 px-1">
                <Label className="text-xs">Anything else we should know?</Label>
                <Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={2} placeholder="Special needs, schedule preferences..." className="rounded-xl text-sm resize-none" data-testid="mobile-reg-message" />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl flex-1" data-testid="mobile-reg-back">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={() => { if (!form.child_name || !form.child_age) { toast.error('Please fill in child name and age'); return; } handleSubmit(); }}
                  disabled={submitting}
                  className="flex-[2] bg-primary hover:bg-primary/90 text-white rounded-xl py-5 font-bold"
                  data-testid="mobile-reg-submit"
                >
                  {submitting ? 'Submitting...' : 'Submit Enrollment'}
                </Button>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MobileRegister;
